import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { evaluatePassword, passwordsMatch } from "../../lib/passwordPolicy";
import PasswordField from "../auth/PasswordField";
import PasswordRules from "../auth/PasswordRules";

/**
 * The password step of the NEW-USER invitation flow.
 *
 * The person arrived through a genuine Supabase Admin invitation link, so they
 * hold a Supabase session but have no password yet. The same five Bulgarian
 * rules as registration and password reset apply, from the one shared helper.
 * The value lives in component state only and is handed straight to the Supabase
 * SDK — it is never sent to the OfferTrace backend and never stored.
 */
export const InviteSetPassword = ({ busy, error, onSubmit }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [localError, setLocalError] = useState("");

  const submit = (event) => {
    event.preventDefault();
    if (busy) return;
    if (!evaluatePassword(password).valid) {
      setLocalError(t("password.error_policy"));
      return;
    }
    if (!passwordsMatch(password, confirmation)) {
      setLocalError(t("password.error_mismatch"));
      return;
    }
    setLocalError("");
    onSubmit(password);
  };

  const shown = localError || error;

  return (
    <form onSubmit={submit} noValidate data-testid="accept-invite-password-form">
      <h1>{t("invite.set_password_title")}</h1>
      <p className="lead">{t("invite.set_password_lead")}</p>

      <PasswordField
        id="accept-invite-password"
        label={t("password.new_password")}
        value={password}
        onChange={(v) => {
          setPassword(v);
          setLocalError("");
        }}
        disabled={busy}
        testId="accept-invite-password"
      />
      <PasswordField
        id="accept-invite-password-confirm"
        label={t("password.repeat_password")}
        value={confirmation}
        onChange={(v) => {
          setConfirmation(v);
          setLocalError("");
        }}
        disabled={busy}
        testId="accept-invite-password-confirm"
      />
      <PasswordRules
        value={password}
        confirmation={confirmation}
        showMatch
        testIdPrefix="accept-invite"
      />

      {shown && (
        <div className="qw-error" data-testid="accept-invite-password-error">
          {shown}
        </div>
      )}

      <button
        type="submit"
        className="qw-btn primary"
        style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
        disabled={busy}
        data-testid="accept-invite-password-submit"
      >
        {busy ? t("invite.accepting") : t("invite.set_password_submit")}
      </button>
    </form>
  );
};

export default InviteSetPassword;
